import { useState, useEffect, useCallback } from 'react';

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isOver: boolean;
}

const MILLISECONDS_IN_SECOND = 1000;
const MILLISECONDS_IN_MINUTE = MILLISECONDS_IN_SECOND * 60;
const MILLISECONDS_IN_HOUR = MILLISECONDS_IN_MINUTE * 60;
const MILLISECONDS_IN_DAY = MILLISECONDS_IN_HOUR * 24;

const timeLeftIsEqual = (left: TimeLeft, right: TimeLeft) =>
  left.days === right.days &&
  left.hours === right.hours &&
  left.minutes === right.minutes &&
  left.seconds === right.seconds &&
  left.isOver === right.isOver;

const useCountdownTimer = (expiryDate: Date): TimeLeft => {
  const calculateTimeLeft = useCallback((): TimeLeft => {
    const now = new Date().getTime();
    const expiration = expiryDate.getTime();
    let difference = expiration - now;

    if (difference < 0) {
      difference = 0;
    }

    const days = Math.floor(difference / MILLISECONDS_IN_DAY);
    const hours = Math.floor((difference % MILLISECONDS_IN_DAY) / MILLISECONDS_IN_HOUR);
    const minutes = Math.floor((difference % MILLISECONDS_IN_HOUR) / MILLISECONDS_IN_MINUTE);
    const seconds = Math.floor((difference % MILLISECONDS_IN_MINUTE) / MILLISECONDS_IN_SECOND);

    return {
      days,
      hours,
      minutes,
      seconds,
      isOver: difference === 0,
    };
  }, [expiryDate]);

  const [timeLeft, setTimeLeft] = useState<TimeLeft>(() => calculateTimeLeft());

  const syncTimeLeft = useCallback(() => {
    setTimeLeft((current) => {
      const next = calculateTimeLeft();
      return timeLeftIsEqual(current, next) ? current : next;
    });
  }, [calculateTimeLeft]);

  useEffect(() => {
    syncTimeLeft();
    const timer = setInterval(syncTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [syncTimeLeft]);

  return timeLeft;
};

export default useCountdownTimer;
